<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card duplicate-projects">
        <h4>Duplicate Projects</h4>
        <p class="hint">Projects that may be the same one, found each night. Keep one and merge the other into it, or dismiss the pair.</p>

        <div v-if="permissionsPending" class="p-text-center"><ProgressSpinner /></div>
        <div v-else-if="!canView" id="noAccess" class="p-invalid">
          {{ permissionsFailed ? 'Your permissions could not be loaded. Please reload the page.' : 'You do not have access to Duplicate Projects.' }}
        </div>

        <div v-else>
          <div id="counts" class="p-d-flex p-flex-wrap p-mb-3">
            <span id="countHigh" class="count count-high">High: {{ counts.HIGH || 0 }}</span>
            <span id="countMedium" class="count count-medium">Medium: {{ counts.MEDIUM || 0 }}</span>
            <span id="countDismissed" class="count count-dismissed">Dismissed: {{ counts.dismissed || 0 }}</span>
          </div>

          <div class="p-fluid p-formgrid p-grid filters">
            <div class="p-field p-col-12 p-md-3">
              <label for="confidence">Confidence</label>
              <Dropdown id="confidence" v-model="filters.confidence" :options="confidences" optionLabel="label"
                optionValue="value" @change="search" />
            </div>
            <div class="p-field p-col-12 p-md-4">
              <label for="searchText">Search</label>
              <InputText id="searchText" v-model="filters.search" placeholder="Project name or address" @keyup.enter="search" />
            </div>
            <div class="p-field p-col-12 p-md-2">
              <label for="postcodeArea">Postcode area</label>
              <InputText id="postcodeArea" v-model="filters.postcodeArea" placeholder="e.g. DN" @keyup.enter="search" />
            </div>
            <div class="p-field-checkbox p-col-12 p-md-3 include-dismissed">
              <Checkbox id="includeDismissed" v-model="filters.includeDismissed" :binary="true" @change="search" />
              <label for="includeDismissed">Include dismissed</label>
            </div>
          </div>
          <div class="p-d-flex p-mb-3">
            <Button id="searchButton" label="Search" icon="pi pi-search" class="p-mr-2" style="width: auto" @click="search" />
            <Button id="clearButton" label="Clear" icon="pi pi-times" class="p-button-text" style="width: auto" @click="clear" />
          </div>

          <div v-if="loading" class="p-text-center"><ProgressSpinner /></div>
          <div v-else-if="loadError" id="loadError" class="p-invalid">{{ loadError }}</div>
          <div v-else-if="!rows.length" id="noPairs" class="hint">No possible duplicate projects match.</div>

          <div v-else>
            <div v-for="pair in rows" :key="pair.pairId" class="pair" :class="{ dismissed: pair.dismissed }"
              :id="'pair-' + pair.pairId">
              <div class="pair-head p-d-flex p-ai-center p-flex-wrap">
                <span class="confidence" :class="'conf-' + String(pair.confidence).toLowerCase()">{{ pair.confidence }}</span>
                <span class="reason p-ml-2">{{ reasonLabel(pair.reason) }}</span>
                <span v-if="pair.dismissed" class="dismissed-label p-ml-2" :id="'dismissedLabel-' + pair.pairId">Dismissed</span>
                <span class="hint p-ml-2 p-mb-0">First seen {{ pair.firstSeen }}</span>
                <span class="p-ml-auto" v-if="canMerge && !pair.dismissed">
                  <Button :id="'dismiss-' + pair.pairId" label="Dismiss" icon="pi pi-eye-slash"
                    class="p-button-text p-mr-2" style="width: auto" :disabled="busyPair === pair.pairId" @click="dismiss(pair)" />
                  <Button :id="'merge-' + pair.pairId" label="Merge" icon="pi pi-clone" style="width: auto"
                    :disabled="busyPair === pair.pairId" @click="openMerge(pair)" />
                </span>
              </div>
              <table class="side-by-side">
                <tbody>
                  <tr v-for="r in sideBySide(pair)" :key="r.label" :class="{ same: r.same }">
                    <th>{{ r.label }}</th>
                    <td>{{ r.a }}</td>
                    <td>{{ r.b }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <Paginator id="paginator" :rows="pageSize" :totalRecords="recCount" :first="(pageNo - 1) * pageSize"
              @page="onPage" />
          </div>
        </div>
      </div>
    </div>

    <Dialog id="mergeDialog" :visible="!!mergePair" header="Merge projects" :modal="true" :style="{ width: '760px' }"
      @update:visible="v => { if (!v) closeMerge() }">
      <div v-if="mergePair">
        <p>Which project do you keep? The other one is merged into it and then deleted.</p>
        <div v-for="p in [mergePair.a, mergePair.b]" :key="p.projectId" class="p-field-radiobutton">
          <RadioButton :id="'keep-' + p.projectId" name="keep" :value="p.projectId" v-model="keepId" @change="loadPreview" />
          <label :for="'keep-' + p.projectId">{{ p.name }} ({{ p.postCode || p.postcodeArea }})</label>
        </div>

        <div v-if="previewLoading" class="p-text-center"><ProgressSpinner /></div>
        <div v-else-if="previewError" id="previewError" class="p-invalid">{{ previewError }}</div>
        <div v-else-if="preview" id="mergePreview">
          <h5>What moves into {{ preview.keep && preview.keep.name }}</h5>
          <ul id="phaseMoves">
            <li v-for="(m, i) in (preview.moves && preview.moves.phases) || []" :key="'ph' + i">{{ phaseMoveText(m) }}</li>
          </ul>
          <ul id="jobMoves">
            <li v-for="(m, i) in (preview.moves && preview.moves.jobs) || []" :key="'job' + i">{{ jobMoveText(m) }}</li>
          </ul>
          <p id="bidMoves">{{ (preview.moves && preview.moves.bids) || 0 }} quotations move.</p>
        </div>

        <div v-if="clashes.length" id="clashes" class="clashes">
          <p><strong>This merge cannot be done yet:</strong></p>
          <ul>
            <li v-for="(c, i) in clashes" :key="'c' + i">{{ clashText(c) }}</li>
          </ul>
        </div>
        <div v-if="mergeError" id="mergeError" class="p-invalid">{{ mergeError }}</div>
      </div>
      <template #footer>
        <Button label="Cancel" icon="pi pi-times" class="p-button-text" style="width: auto" @click="closeMerge" />
        <Button id="confirmMerge" label="Merge" icon="pi pi-check" style="width: auto"
          :disabled="!mergeAllowed || merging" @click="confirmMerge" />
      </template>
    </Dialog>
  </div>
</template>

<script>
import api from './sharedEnquiryApi'
import { errorText } from './sharedEnquiryLogic'
import {
  canMergeProjects, CONFIDENCES, PAGE_SIZE, emptyFilters, pairParams, sideBySide, reasonLabel, otherOf,
  phaseMoveText, jobMoveText, clashText, canConfirmMerge, refusalOf
} from './duplicateProjectsLogic'

export default {
  name: 'DuplicateProjects',
  data: () => ({
    confidences: CONFIDENCES,
    filters: emptyFilters(),
    counts: {},
    rows: [],
    recCount: 0,
    pageNo: 1,
    pageSize: PAGE_SIZE,
    loading: false,
    loadError: '',
    busyPair: null,
    mergePair: null,
    keepId: null,
    preview: null,
    previewLoading: false,
    previewError: '',
    clashes: [],
    mergeError: '',
    merging: false,
    started: false
  }),
  computed: {
    myPermissions() {
      return this.$store.getters['auth/getUserPermissions'] || []
    },
    // Permissions are not loaded yet on a hard refresh: wait for them rather than refuse (as Duplicate Enquiries)
    permissionsStatus() {
      return this.$store.getters['auth/getUserPermissionsStatus']
    },
    permissionsPending() {
      return !this.canView && this.myPermissions.length === 0 && this.permissionsStatus !== 'loaded' && this.permissionsStatus !== 'error'
    },
    permissionsFailed() {
      return this.myPermissions.length === 0 && this.permissionsStatus === 'error'
    },
    canView() {
      return canMergeProjects(this.myPermissions, this.$store.getters['auth/getMyProfile'])
    },
    // the server's word on the buttons (counts.canMerge), and the page gate as well
    canMerge() {
      return this.canView && this.counts.canMerge === true
    },
    mergeAllowed() {
      return canConfirmMerge(this.preview) && !this.clashes.length
    }
  },
  watch: {
    canView: { immediate: true, handler(v) { if (v && !this.started) this.start() } }
  },
  methods: {
    sideBySide,
    reasonLabel,
    phaseMoveText,
    jobMoveText,
    clashText,
    start() {
      this.started = true
      this.loadCounts()
      this.loadPairs()
    },
    loadCounts() {
      return api.duplicateCounts()
        .then(c => { this.counts = c || {} })
        .catch(e => {
          this.$toast.add({ severity: 'error', summary: 'Counts not loaded', detail: errorText(e, 'The counts could not be loaded.'), life: 5000 })
        })
    },
    loadPairs() {
      this.loading = true
      return api.duplicatePairs(pairParams(this.filters, this.pageNo, this.pageSize))
        .then(({ rows, recCount }) => {
          this.rows = rows
          this.recCount = recCount
          this.loadError = ''
        })
        .catch(e => {
          this.rows = []
          this.recCount = 0
          this.loadError = errorText(e, 'The duplicate projects could not be loaded.')
        })
        .finally(() => { this.loading = false })
    },
    search() {
      this.pageNo = 1
      return this.loadPairs()
    },
    clear() {
      this.filters = emptyFilters()
      return this.search()
    },
    onPage(e) {
      this.pageNo = e.page + 1
      this.pageSize = e.rows
      return this.loadPairs()
    },
    async dismiss(pair) {
      this.busyPair = pair.pairId
      try {
        await api.dismissPair(pair.pairId)
        this.$toast.add({ severity: 'success', summary: 'Dismissed', detail: 'The pair will not be listed again.', life: 4000 })
        await Promise.all([this.loadCounts(), this.loadPairs()])
      } catch (e) {
        this.$toast.add({ severity: 'error', summary: 'Not dismissed', detail: refusalOf(e, 'The pair could not be dismissed.').message, life: 6000 })
      } finally {
        this.busyPair = null
      }
    },
    openMerge(pair) {
      this.mergePair = pair
      this.keepId = null
      this.preview = null
      this.previewError = ''
      this.clashes = []
      this.mergeError = ''
    },
    closeMerge() {
      this.mergePair = null
      this.preview = null
    },
    loadPreview() {
      const other = otherOf(this.mergePair, this.keepId)
      if (!other) return Promise.resolve()
      this.previewLoading = true
      this.preview = null
      this.previewError = ''
      this.mergeError = ''
      this.clashes = []
      return api.mergePreview(this.keepId, other.projectId)
        .then(p => {
          this.preview = p
          this.clashes = (p && p.clashes) || []
        })
        .catch(e => { this.previewError = refusalOf(e, 'The merge preview could not be loaded.').message })
        .finally(() => { this.previewLoading = false })
    },
    async confirmMerge() {
      const other = otherOf(this.mergePair, this.keepId)
      if (!other || !this.mergeAllowed) return
      this.merging = true
      this.mergeError = ''
      try {
        await api.mergeProjects(this.keepId, other.projectId)
        this.$toast.add({ severity: 'success', summary: 'Projects merged', detail: other.name + ' was merged into ' + (this.preview.keep && this.preview.keep.name) + '.', life: 6000 })
        this.closeMerge()
        await Promise.all([this.loadCounts(), this.loadPairs()])
      } catch (e) {
        // a 409 brings the clashes found under the lock; a 403 its refusal text
        const r = refusalOf(e, 'The projects could not be merged.')
        this.mergeError = r.message
        if (r.clashes.length) this.clashes = r.clashes
      } finally {
        this.merging = false
      }
    }
  }
}
</script>

<style scoped>
.duplicate-projects .hint { display: block; color: #6c757d; margin-bottom: 0.5rem; }
.duplicate-projects span.hint { display: inline; }
.duplicate-projects .count { padding: 0.25rem 0.75rem; border-radius: 1rem; margin-right: 0.5rem; background: #eef2f7; font-weight: 600; }
.duplicate-projects .count-high { background: #fde2e1; }
.duplicate-projects .count-medium { background: #fff3cd; }
.duplicate-projects .include-dismissed { align-self: flex-end; }
.duplicate-projects .pair { border: 1px solid #dee2e6; border-radius: 4px; padding: 0.75rem; margin-bottom: 1rem; }
.duplicate-projects .pair.dismissed { opacity: 0.55; background: #f8f9fa; }
.duplicate-projects .dismissed-label { font-weight: 600; color: #6c757d; text-transform: uppercase; font-size: 0.8rem; }
.duplicate-projects .confidence { font-weight: 700; }
.duplicate-projects .conf-high { color: #c62828; }
.duplicate-projects .conf-medium { color: #b26a00; }
.duplicate-projects .side-by-side { width: 100%; border-collapse: collapse; margin-top: 0.5rem; table-layout: fixed; }
.duplicate-projects .side-by-side th { text-align: left; width: 9rem; color: #6c757d; font-weight: 600; }
.duplicate-projects .side-by-side th, .duplicate-projects .side-by-side td { padding: 0.25rem 0.5rem; border-top: 1px solid #f1f3f5; overflow-wrap: anywhere; }
.duplicate-projects .side-by-side tr.same td { background: #fff8e1; }
.clashes { color: #c62828; border-left: 3px solid #c62828; padding-left: 0.75rem; margin-top: 0.75rem; }
</style>
